'use client'

import { Check, ChevronDown } from 'lucide-react'

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { MainNavOrganization } from './types'
import { PremiumPill } from '../premium-pill'

export interface MainNavOrganizationPickerProps {
  organizations: MainNavOrganization[]
  activeId: string
  onSelect: (organizationId: string) => void
  /** Heading above the list. */
  heading?: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
  className?: string
}

/**
 * The school picker (Daftar sekolah), presentational only: the same look as `SwitchOrganization`,
 * but the list comes in as data and the switch goes out through `onSelect`. Switching makes the menu,
 * Beranda, Kelas Saya and Pesan follow the selected school (PIB-35); that is the app's job.
 */
export function MainNavOrganizationPicker({
  organizations,
  activeId,
  onSelect,
  heading = 'Daftar sekolah',
  open,
  onOpenChange,
  className,
}: MainNavOrganizationPickerProps) {
  const active = organizations.find((organization) => organization.id === activeId)
  if (!active) return null

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger
        render={(props) => (
          <Button
            baseButton
            className={cn(
              'srd:w-full srd:max-w-[240px] srd:justify-between srd:px-3 srd:py-2 srd:bg-neutral-white srd:text-neutral-black srd:rounded-lg srd:border srd:border-outline-variant srd:focus:outline-blue-100',
              className,
            )}
            {...props}
          >
            <span className='srd:flex srd:min-w-0 srd:flex-1 srd:items-center srd:gap-x-2'>
              <Avatar profile='organization' size='sm' className='srd:shrink-0'>
                <AvatarImage src={active.logo ?? undefined} alt={active.name} />
                <AvatarFallback>{getInitials(active.name)}</AvatarFallback>
              </Avatar>
              <span className='srd:min-w-0 srd:truncate srd:font-medium'>{active.name}</span>
            </span>
            <ChevronDown className='srd:size-4 srd:text-blue-100' />
          </Button>
        )}
      />

      <DropdownMenuContent
        className='srd:w-(--anchor-width) srd:min-w-[300px] srd:rounded-lg'
        align='start'
        side='bottom'
        sideOffset={4}
      >
        <DropdownMenuGroup>
          <DropdownMenuLabel>{heading}</DropdownMenuLabel>
          {organizations.map((organization) => {
            const isActive = organization.id === activeId
            return (
              <DropdownMenuItem
                key={organization.id}
                disabled={isActive}
                onClick={() => onSelect(organization.id)}
                className={cn(isActive && 'srd:focus:bg-transparent srd:data-disabled:opacity-100')}
              >
                <Avatar profile='organization'>
                  <AvatarImage src={organization.logo ?? undefined} alt={organization.name} />
                  <AvatarFallback>{getInitials(organization.name)}</AvatarFallback>
                </Avatar>
                <span className='srd:flex srd:min-w-0 srd:flex-1 srd:flex-col'>
                  <span className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:truncate')}>
                    {organization.name}
                  </span>
                  {organization.subtitle && (
                    <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100')}>
                      {organization.subtitle}
                    </span>
                  )}
                </span>
                <PremiumPill isPremium={organization.isPremium} />
                {isActive && <Check className='srd:size-4 srd:shrink-0 srd:text-blue-100' />}
              </DropdownMenuItem>
            )
          })}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
